import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import CustomTextInput from '@/components/form/CustomTextInput';
import CustomCheckbox from '@/components/form/CustomCheckbox';

import { userSignupCompleteSchema, UserSignupCompleteFormValues } from '../schemas/userSignupCompleteSchema';
import CustomPhoneIntlTelInput from '@/components/form/CustomPhoneIntlTelInput';
import CustomPasswordInput from '@/components/form/CustomPasswordInput';
import ErrorMessage from '@/components/common/ErrorMessage';
import { BasicResponse } from '@/core/models/common-types';
import { UserSignupState, UserSignupAction } from '../states/userSignupReducer';
import { useApp } from '@/contexts/AppContext';
import axios from 'axios';
import { extractErrorMessage } from '@/core/utils/utils';

import { MessageBox } from '@/core/utils/message-box';
import { OtpChannel } from '@/core/enums/otp-channel.enum';
import CustomTurnstile from '@/components/form/CustomTurnstile';

interface UserSignupCompleteStepProps {
  id: string;
  onPrevious: () => void;
  onSubmit: (data: UserSignupCompleteFormValues) => void;
  state: UserSignupState;
  dispatch: React.Dispatch<UserSignupAction>;
}

const UserSignupCompleteStep: React.FC<UserSignupCompleteStepProps> = ({ id, onPrevious, onSubmit, state }) => {
  const [error, setError] = useState<BasicResponse | null>(null);
  const { baseUrl } = useApp();
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UserSignupCompleteFormValues>({
    resolver: zodResolver(userSignupCompleteSchema),
    defaultValues: {
      id,
      firstName: state.firstName,
      surname: state.surname,
      otherNames: state.otherNames,
      email: state.email,
      phoneNumber: state.phoneNumber,
      pwd: false,
      ncpwdRegNo: '',
      password: '',
      confirmPassword: '',
    },
  });

  const pwd = useWatch({
    control,
    name: 'pwd',
  });

  useEffect(() => {
    console.log('Form Errors =>', errors);
  }, [errors]);

  const submitForm = async (data: UserSignupCompleteFormValues) => {
    try {
      await axios.post<BasicResponse>(`${baseUrl}api/v2/UserSignup/CompleteSignup`, data);
      onSubmit(data);
      const result = await MessageBox.alertSuccess(
        'Your registration has been completed successfully. You may now sign in.',
        'Registration Complete'
      );
      if (result.isConfirmed) {
        window.location.href = `${baseUrl}/auth/login`;
      }
    } catch (error) {
      setError(extractErrorMessage(error));
       setTurnstileResetKey((current) => current + 1);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <ErrorMessage error={error} onClose={() => setError(null)} />
        <form onSubmit={handleSubmit(submitForm)}>
          <h5 className="mb-3">Complete Signup</h5>

          <CustomTextInput
            control={control}
            name="firstName"
            label="First Name"
            placeholder="First Name"
            required
            readonly
          />
          <CustomTextInput control={control} name="surname" label="Surname" required readonly />
          <CustomTextInput control={control} name="otherNames" label="Other Names" readonly />

          <CustomTextInput
            control={control}
            name="email"
            label="Email Address"
            required
            placeholder="Enter Email Address"
            readonly={state.otpChannel == OtpChannel.Email}
          />

          <CustomPhoneIntlTelInput
            control={control}
            name="phoneNumber"
            label="Phone Number"
            required
            disabled={state.otpChannel == OtpChannel.Sms}
          />

          <div className="mt-2 mb-2">
            <CustomCheckbox control={control} name="pwd" label="I am a person with disability (PWD)" />
          </div>

          {pwd && (
            <CustomTextInput
              control={control}
              name="ncpwdRegNo"
              label="NCPWD Registration Number"
              placeholder="NCPWD Registration Number"
              required
            />
          )}

          <CustomPasswordInput control={control} name="password" label="Password" placeholder="Password" required />

          <CustomPasswordInput
            control={control}
            name="confirmPassword"
            label="Confirm Password"
            placeholder="Confirm Password"
            required
          />

          <CustomTurnstile
            name="turnstileToken"
            control={control}
            action="complete_signup"
            resetKey={turnstileResetKey}
          />
          <div className="d-flex justify-content-between mt-4">
            <button type="button" className="btn btn-outline-secondary" onClick={onPrevious}>
              Previous
            </button>

            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              Complete Signup
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserSignupCompleteStep;
